import { Settings } from "@mui/icons-material";
import { Box, Button, Chip, Skeleton, Typography } from "@mui/material";
import React from "react";
import { useChat } from "../../../context/ChatProvider";
import { MessageProps } from "../../../interfaces/all";
import { resolveMediaUrl } from "../../../utils/media";
import MessageAvatar from "./MessageAvatar";
import MessageMetaInfo from "./MessageMetaInfo";
import {
  MessageContainer,
  MessageContent,
  MessageSender,
  MessageTextLine,
} from "./styles";

interface TemplateMessagePropsSkeleton extends MessageProps {
  showSkeleton?: boolean;
}

const TemplateMessage: React.FC<TemplateMessagePropsSkeleton> = React.memo(
  ({ message, showAvatar = true, showDate = true, showSkeleton = false }) => {
    const isFromClient = message.sentBy === "client";
    const showTriangle = showAvatar;
    const { selectedConversation } = useChat();

    // Verificar se é mensagem do sistema baseado nos parâmetros do template
    const isSystemMessage = () => {
      const templateData = message.content?.templateData;
      if (!templateData?.components) return false;

      const bodyComponent = templateData.components.find(
        (comp: any) => comp.type === "body",
      );
      if (!bodyComponent?.parameters) return false;

      // Verificar se algum parâmetro contém "Sistema"
      return (
        Array.isArray(bodyComponent.parameters) &&
        bodyComponent.parameters.some(
          (param: any) => param.type === "text" && param.text === "Sistema",
        )
      );
    };

    const isFromSystem = isSystemMessage();

    const getSenderName = React.useCallback(() => {
      const formatName = (name: string) => {
        return name
          .split(" ")
          .map(
            (word) =>
              word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
          )
          .join(" ");
      };

      let name: string;
      switch (message.sentBy) {
        case "client":
          name =
            selectedConversation?.valid_name ||
            selectedConversation?.name ||
            "Cliente";
          return formatName(name);
        case "user":
          name = selectedConversation?.attendant_name || "Atendente";
          return formatName(name);
        default:
          return "Usuário";
      }
    }, [
      message.sentBy,
      selectedConversation?.valid_name,
      selectedConversation?.name,
      selectedConversation?.attendant_name,
    ]);

    if (showSkeleton) {
      return (
        <MessageContainer
          isFromClient={isFromClient}
          showTriangle={showTriangle}
        >
          {isFromClient ? (
            <>
              <MessageAvatar sentBy={message.sentBy} showAvatar={showAvatar} />
              <MessageContent
                isFromClient={isFromClient}
                showTriangle={showTriangle}
              >
                {showAvatar && (
                  <MessageSender isFromClient={isFromClient}>
                    <Skeleton variant="text" width={80} height={18} />
                  </MessageSender>
                )}
                <MessageTextLine
                  style={{
                    position: "relative",
                    paddingRight: 62,
                    minHeight: 24,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      minHeight: 40,
                    }}
                  >
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      height={120}
                      sx={{ borderRadius: 1 }}
                    />
                    {/* Nome do template com indicativo de sistema */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Skeleton variant="text" width="40%" height={16} />
                      {isFromSystem && (
                        <Chip
                          icon={<Settings sx={{ fontSize: "0.6rem" }} />}
                          label="Sistema"
                          size="small"
                          sx={{
                            height: 16,
                            fontSize: "0.6rem",
                            backgroundColor: "#e3f2fd",
                            color: "#1976d2",
                            "& .MuiChip-icon": {
                              fontSize: "0.6rem",
                            },
                          }}
                        />
                      )}
                    </Box>
                    <Skeleton variant="text" width="80%" />
                    <Skeleton variant="text" width="60%" />
                    <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                      <Skeleton
                        variant="rectangular"
                        width={100}
                        height={32}
                        sx={{ borderRadius: 1 }}
                      />
                      <Skeleton
                        variant="rectangular"
                        width={120}
                        height={32}
                        sx={{ borderRadius: 1 }}
                      />
                    </Box>

                    {/* Timestamp após toda a mensagem */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mt: 1,
                      }}
                    >
                      <MessageMetaInfo
                        message={message}
                        showDate={showDate}
                        isFromClient={isFromClient}
                      />
                    </Box>
                  </Box>
                </MessageTextLine>
              </MessageContent>
            </>
          ) : (
            <>
              <MessageContent
                isFromClient={isFromClient}
                showTriangle={showTriangle}
              >
                {showAvatar && (
                  <MessageSender isFromClient={isFromClient}>
                    <Skeleton variant="text" width={80} height={18} />
                  </MessageSender>
                )}
                <MessageTextLine
                  style={{
                    position: "relative",
                    paddingRight: 62,
                    minHeight: 24,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      minHeight: 40,
                    }}
                  >
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      height={120}
                      sx={{ borderRadius: 1 }}
                    />
                    {/* Nome do template com indicativo de sistema */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Skeleton variant="text" width="40%" height={16} />
                      {isFromSystem && (
                        <Chip
                          icon={<Settings sx={{ fontSize: "0.6rem" }} />}
                          label="Sistema"
                          size="small"
                          sx={{
                            height: 16,
                            fontSize: "0.6rem",
                            backgroundColor: "#e3f2fd",
                            color: "#1976d2",
                            "& .MuiChip-icon": {
                              fontSize: "0.6rem",
                            },
                          }}
                        />
                      )}
                    </Box>
                    <Skeleton variant="text" width="80%" />
                    <Skeleton variant="text" width="60%" />
                    <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                      <Skeleton
                        variant="rectangular"
                        width={100}
                        height={32}
                        sx={{ borderRadius: 1 }}
                      />
                      <Skeleton
                        variant="rectangular"
                        width={120}
                        height={32}
                        sx={{ borderRadius: 1 }}
                      />
                    </Box>

                    {/* Timestamp após toda a mensagem */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mt: 1,
                      }}
                    >
                      <MessageMetaInfo
                        message={message}
                        showDate={showDate}
                        isFromClient={isFromClient}
                      />
                    </Box>
                  </Box>
                </MessageTextLine>
              </MessageContent>
              <MessageAvatar sentBy={message.sentBy} showAvatar={showAvatar} />
            </>
          )}
        </MessageContainer>
      );
    }

    const renderTemplateContent = () => {
      const templateData = message.content?.templateData;

      if (!templateData || typeof templateData !== "object") {
        return (
          <MessageContainer
            isFromClient={isFromClient}
            showTriangle={showTriangle}
          >
            {isFromClient ? (
              <>
                <MessageAvatar
                  sentBy={message.sentBy}
                  showAvatar={showAvatar}
                />
                <MessageContent
                  isFromClient={isFromClient}
                  showTriangle={showTriangle}
                >
                  {showAvatar && (
                    <MessageSender isFromClient={isFromClient}>
                      {getSenderName()}
                    </MessageSender>
                  )}
                  <MessageTextLine
                    style={{ position: "relative", minHeight: 24 }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Template inválido ou não encontrado
                    </Typography>

                    {/* Timestamp após toda a mensagem */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mt: 1,
                      }}
                    >
                      <MessageMetaInfo
                        message={message}
                        showDate={showDate}
                        isFromClient={isFromClient}
                      />
                    </Box>
                  </MessageTextLine>
                </MessageContent>
              </>
            ) : (
              <>
                <MessageContent
                  isFromClient={isFromClient}
                  showTriangle={showTriangle}
                >
                  {showAvatar && (
                    <MessageSender isFromClient={isFromClient}>
                      {getSenderName()}
                    </MessageSender>
                  )}
                  <MessageTextLine
                    style={{ position: "relative", minHeight: 24 }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Template inválido ou não encontrado
                    </Typography>

                    {/* Timestamp após toda a mensagem */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mt: 1,
                      }}
                    >
                      <MessageMetaInfo
                        message={message}
                        showDate={showDate}
                        isFromClient={isFromClient}
                      />
                    </Box>
                  </MessageTextLine>
                </MessageContent>
                <MessageAvatar
                  sentBy={message.sentBy}
                  showAvatar={showAvatar}
                />
              </>
            )}
          </MessageContainer>
        );
      }

      // Processar dados do template
      const templateName = templateData.templateName || "Template";

      // Extrair componentes processados
      const components = Array.isArray(templateData.components)
        ? templateData.components
        : [];

      // Processar header (imagem)
      let headerImage = null;
      const originalComponents = Array.isArray(
        (templateData as any).originalComponents,
      )
        ? (templateData as any).originalComponents
        : [];
      const headerComponent = originalComponents.find(
        (comp: any) => comp.type === "HEADER",
      );
      if (
        headerComponent?.format === "IMAGE" &&
        headerComponent?.example?.header_handle?.[0]
      ) {
        headerImage = resolveMediaUrl(headerComponent.example.header_handle[0]);
      }

      // Processar body (texto com parâmetros substituídos)
      let bodyText = "";
      const bodyComponent = components.find(
        (comp: any) => comp.type === "body",
      );
      if (
        bodyComponent?.parameters &&
        Array.isArray(bodyComponent.parameters)
      ) {
        // Se temos parâmetros processados, usar o texto original e substituir
        const originalBody = templateData.body || "";
        let processedText = originalBody;

        // Substituir {{1}}, {{2}}, {{3}} pelos valores dos parâmetros
        bodyComponent.parameters.forEach((param: any, index: number) => {
          if (param.type === "text" && param.text) {
            processedText = processedText.replace(
              `{{${index + 1}}}`,
              param.text,
            );
          }
        });

        bodyText = processedText;
      } else if (templateData.body) {
        bodyText = templateData.body;
      }

      // Processar botões
      const buttons = Array.isArray(templateData.buttons)
        ? templateData.buttons
        : [];

      return (
        <MessageContainer
          isFromClient={isFromClient}
          showTriangle={showTriangle}
        >
          {isFromClient ? (
            <>
              <MessageAvatar sentBy={message.sentBy} showAvatar={showAvatar} />
              <MessageContent
                isFromClient={isFromClient}
                showTriangle={showTriangle}
                style={{ maxWidth: headerImage ? 300 : "none" }}
              >
                {showAvatar && (
                  <MessageSender isFromClient={isFromClient}>
                    {getSenderName()}
                  </MessageSender>
                )}
                <MessageTextLine
                  style={{ position: "relative", minHeight: 24 }}
                >
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    {/* Nome do template */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: "0.7rem", fontWeight: "bold" }}
                      >
                        📄 {templateName}
                      </Typography>
                      {/* Indicativo de envio via sistema */}
                      {isFromSystem && (
                        <Chip
                          icon={<Settings sx={{ fontSize: "0.6rem" }} />}
                          label="Sistema"
                          size="small"
                          sx={{
                            height: 16,
                            fontSize: "0.6rem",
                            backgroundColor: "#e3f2fd",
                            color: "#1976d2",
                            "& .MuiChip-icon": {
                              fontSize: "0.6rem",
                            },
                          }}
                        />
                      )}
                    </Box>

                    {/* Header com imagem */}
                    {headerImage && (
                      <Box sx={{ mb: 1 }}>
                        <img
                          src={headerImage}
                          alt="Template header"
                          style={{
                            width: "100%",
                            maxWidth: 300,
                            height: "auto",
                            borderRadius: 8,
                            objectFit: "cover",
                          }}
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display =
                              "none";
                          }}
                        />
                      </Box>
                    )}

                    {/* Texto do corpo */}
                    {bodyText && (
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                      >
                        {bodyText}
                      </Typography>
                    )}

                    {/* Botões */}
                    {buttons.length > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                          mt: 1,
                        }}
                      >
                        {buttons.map((button: any, index: number) => (
                          <Button
                            key={index}
                            variant="outlined"
                            size="small"
                            disabled
                            sx={{
                              fontSize: "0.75rem",
                              textTransform: "none",
                              justifyContent: "flex-start",
                              borderColor: "#e0e0e0",
                              color: "#666",
                              "&:disabled": {
                                backgroundColor: "#f5f5f5",
                                color: "#999",
                              },
                            }}
                          >
                            {button.text}
                          </Button>
                        ))}
                      </Box>
                    )}

                    {/* Timestamp após toda a mensagem */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mt: 2,
                      }}
                    >
                      <MessageMetaInfo
                        message={message}
                        showDate={showDate}
                        isFromClient={isFromClient}
                      />
                    </Box>
                  </Box>
                </MessageTextLine>
              </MessageContent>
            </>
          ) : (
            <>
              <MessageContent
                isFromClient={isFromClient}
                showTriangle={showTriangle}
                style={{ maxWidth: headerImage ? 300 : "none" }}
              >
                {showAvatar && (
                  <MessageSender isFromClient={isFromClient}>
                    {getSenderName()}
                  </MessageSender>
                )}
                <MessageTextLine
                  style={{ position: "relative", minHeight: 24 }}
                >
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    {/* Nome do template */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: "0.7rem", fontWeight: "bold" }}
                      >
                        📄 {templateName}
                      </Typography>
                      {/* Indicativo de envio via sistema */}
                      {isFromSystem && (
                        <Chip
                          icon={<Settings sx={{ fontSize: "0.6rem" }} />}
                          label="Sistema"
                          size="small"
                          sx={{
                            height: 16,
                            fontSize: "0.6rem",
                            backgroundColor: "#e3f2fd",
                            color: "#1976d2",
                            "& .MuiChip-icon": {
                              fontSize: "0.6rem",
                            },
                          }}
                        />
                      )}
                    </Box>

                    {/* Header com imagem */}
                    {headerImage && (
                      <Box sx={{ mb: 1 }}>
                        <img
                          src={headerImage}
                          alt="Template header"
                          style={{
                            width: "100%",
                            maxWidth: 300,
                            height: "auto",
                            borderRadius: 8,
                            objectFit: "cover",
                          }}
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display =
                              "none";
                          }}
                        />
                      </Box>
                    )}

                    {/* Texto do corpo */}
                    {bodyText && (
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                      >
                        {bodyText}
                      </Typography>
                    )}

                    {/* Botões */}
                    {buttons.length > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                          mt: 1,
                        }}
                      >
                        {buttons.map((button: any, index: number) => (
                          <Button
                            key={index}
                            variant="outlined"
                            size="small"
                            disabled
                            sx={{
                              fontSize: "0.75rem",
                              textTransform: "none",
                              justifyContent: "flex-start",
                              borderColor: "#e0e0e0",
                              color: "#666",
                              "&:disabled": {
                                backgroundColor: "#f5f5f5",
                                color: "#999",
                              },
                            }}
                          >
                            {button.text}
                          </Button>
                        ))}
                      </Box>
                    )}

                    {/* Timestamp após toda a mensagem */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mt: 2,
                        minWidth: "100%",
                      }}
                    >
                      <MessageMetaInfo
                        message={message}
                        showDate={showDate}
                        isFromClient={isFromClient}
                        readStatus={message.readStatus || { isRead: false }}
                        sx={{
                          position: "static",
                          right: "auto",
                          bottom: "auto",
                          height: "auto",
                          minWidth: "auto",
                        }}
                      />
                    </Box>
                  </Box>
                </MessageTextLine>
              </MessageContent>
              <MessageAvatar sentBy={message.sentBy} showAvatar={showAvatar} />
            </>
          )}
        </MessageContainer>
      );
    };

    return renderTemplateContent();
  },
);

TemplateMessage.displayName = "TemplateMessage";

export default TemplateMessage;
